import { useMemo, useState } from "react";
import type { Drama } from "@/types/drama";

export type SortKey = "latest" | "hot" | "rating";

export const sortLabels: Record<SortKey, string> = {
  latest: "按最新上线",
  hot: "按热度排序",
  rating: "按评分排序",
};

export function useDramaFilter(source: Drama[], initialTypes: string[] = []) {
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialTypes);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("hot");

  const toggleType = (value: string) => {
    setSelectedTypes((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
  };

  const toggleYear = (value: number) => {
    setSelectedYears((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
  };

  const toggleRegion = (value: string) => {
    setSelectedRegions((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
  };

  const reset = () => {
    setSelectedTypes([]);
    setSelectedYears([]);
    setSelectedRegions([]);
  };

  const results = useMemo(() => {
    const filtered = source.filter((drama) => {
      const typeMatch =
        selectedTypes.length === 0 || drama.types.some((type) => selectedTypes.includes(type));
      const yearMatch =
        selectedYears.length === 0 || selectedYears.includes(drama.year);
      const regionMatch =
        selectedRegions.length === 0 || selectedRegions.includes(drama.region);
      return typeMatch && yearMatch && regionMatch;
    });

    return [...filtered].sort((a, b) => {
      if (sortKey === "latest") return b.year - a.year || b.heat - a.heat;
      if (sortKey === "rating") return b.rating - a.rating || b.heat - a.heat;
      return b.heat - a.heat;
    });
  }, [source, selectedTypes, selectedYears, selectedRegions, sortKey]);

  const activeCount = selectedTypes.length + selectedYears.length + selectedRegions.length;

  return {
    selectedTypes,
    setSelectedTypes,
    selectedYears,
    selectedRegions,
    sortKey,
    setSortKey,
    toggleType,
    toggleYear,
    toggleRegion,
    reset,
    results,
    activeCount,
  };
}